import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport } from '@feathr/blackbox';
import { ProgressBar } from '@feathr/components';

import { getProgressMode } from './ExportProgressCell.utils';

import * as styles from './ExportProgressCell.css';

interface IExportProgressCellProps {
  segmentExport: SegmentExport;
}

function ExportProgressCell({ segmentExport }: Readonly<IExportProgressCellProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const mode = getProgressMode({
    state: segmentExport.get('state'),
    progress: segmentExport.get('progress'),
  });

  if (mode.kind === 'notApplicable') {
    return <span className={styles.label}>{t('N/A')}</span>;
  }

  if (mode.kind === 'calculating') {
    return <span className={styles.label}>{t('Calculating…')}</span>;
  }

  if (mode.kind === 'complete') {
    return (
      <Stack align={'flex-start'} gap={theme.spacing['1']} w={'100%'}>
        <ProgressBar aria-label={t('Export progress')} shade={600} theme={'green'} value={100} />
        <span className={styles.percentComplete}>{t('{{percent}}%', { percent: 100 })}</span>
      </Stack>
    );
  }

  return (
    <Stack align={'flex-start'} gap={theme.spacing['1']} w={'100%'}>
      <ProgressBar
        aria-label={t('Export progress')}
        shade={600}
        theme={'yellow'}
        value={mode.percent}
      />
      <span className={styles.percent}>{t('{{percent}}%', { percent: mode.percent })}</span>
    </Stack>
  );
}

export default observer(ExportProgressCell);
